import { useMemo } from 'react';

import {
  loopsOnLayer,
  selectionOf,
  summarizeSelection,
} from '@frontend/viewer-core';

import type { DrawingModel, DxfViewerSession } from '../session/types';
import { useReadableStore } from '../session/useSessionState';
import { entityTypeLabel } from '../utils/entityTypeLabel';
import {
  formatAngle,
  formatArea,
  formatLength,
  formatSize,
  type MeasurementFormat,
} from '../utils/formatMeasurement';

/** The last pick's rows, already formatted. */
export type DxfSelectionData = {
  /** Display label for the pick's one source entity, or `null` when it spans several. */
  type: string | null;
  length: string;
  /** Diameter and angle when the pick lies on one circle, else `null`. */
  arc: { diameter: string; angle: string } | null;
};

export type DxfDetailsOverlayState = {
  size: string | null;
  hasDetail: boolean;
  heading: string;
  count: number | null;
  length: string;
  area: string | null;
  selectionData: DxfSelectionData | null;
};

/**
 * Connects `DxfDetailsOverlay` to the session. Call only under a ready drawing —
 * empty bounds, or a hovered layer with no geometry to describe, returns `null`
 * so the overlay can hide, which is not a load state.
 */
export const useDxfDetailsOverlay = (
  session: DxfViewerSession,
  model: DrawingModel,
  format: MeasurementFormat,
): DxfDetailsOverlayState | null => {
  const selectionSummary = useReadableStore(session.selection.summary);
  const lastPick = useReadableStore(session.selection.lastPickSummary);
  const hoveredLayer = useReadableStore(session.hoveredLayer);

  // Memoised because `countRuns` flood-fills the welded graph, and this hook
  // would otherwise redo that walk on every render this component takes for
  // any reason, not only the ones where the hover actually moved.
  const hoveredLayerSummary = useMemo(
    () =>
      hoveredLayer === null
        ? null
        : summarizeSelection(
            selectionOf(loopsOnLayer(model.loops, hoveredLayer)),
            model.loops,
            model.chunkGraph,
          ),
    [hoveredLayer, model],
  );

  const bounds = model.bounds;
  if (!bounds) {
    return null;
  }

  const [minX, minY, maxX, maxY] = bounds;

  // A hovered layer takes precedence: the user is asking about it right now.
  const isLayerHovered = hoveredLayer !== null;
  const summary = hoveredLayerSummary ?? selectionSummary;
  const heading = isLayerHovered
    ? model.scene.layers[hoveredLayer]?.name || 'Layer'
    : 'Cumulative Selection';
  const hasDetail = summary.count > 0;

  if (isLayerHovered && !hasDetail) {
    return null;
  }

  return {
    size:
      isLayerHovered || hasDetail
        ? null
        : formatSize(maxX - minX, maxY - minY, format),
    hasDetail,
    heading,
    count: isLayerHovered ? summary.count : null,
    length: hasDetail ? formatLength(summary.length, format) : '',
    area: summary.area === null ? null : formatArea(summary.area, format),
    // Hidden while a layer is hovered: the rows above describe the layer then,
    // and the last pick sitting under them would read as part of it.
    selectionData:
      hoveredLayer === null && lastPick !== null
        ? {
            type:
              lastPick.entityType === null
                ? null
                : entityTypeLabel(lastPick.entityType),
            length: formatLength(lastPick.length, format),
            arc:
              lastPick.arc === null
                ? null
                : {
                    diameter: formatLength(lastPick.arc.diameter, format),
                    angle: formatAngle(lastPick.arc.sweep, format),
                  },
          }
        : null,
  };
};
